"use client";

import { useEffect, useMemo, useSyncExternalStore } from "react";
import { useTenant } from "@/src/contexts/TenantContext";
import { useAuth } from "@/src/contexts/AuthContext";
import { getAddresses } from "@/src/services/address.service";
import {
  addressLocationText,
  getSelectedCity,
  getSelectedCoordsRaw,
  getServiceArea,
  getServiceAreaVersion,
  isCityServiceable,
  loadServiceArea,
  parseCoords,
  setSelectedCity,
  SERVICE_AREA_LOADED,
  type ServiceArea,
} from "@/src/constant/serviceArea/serviceArea";

interface UseServiceArea {
  /** Set only for outlets that restrict where they deliver. */
  area: ServiceArea | null;
  /** Location text of the currently selected address, if any. */
  city: string | null;
  /** True when the outlet is restricted and the selected address is outside it. */
  blocked: boolean;
}

/** Header dispatches `selected_city_changed` whenever the buyer switches address. */
function subscribe(onChange: () => void) {
  window.addEventListener("selected_city_changed", onChange);
  window.addEventListener("storage", onChange);
  return () => {
    window.removeEventListener("selected_city_changed", onChange);
    window.removeEventListener("storage", onChange);
  };
}

/** Re-read when a store's area arrives from product-service. */
function subscribeAreas(onChange: () => void) {
  window.addEventListener(SERVICE_AREA_LOADED, onChange);
  return () => window.removeEventListener(SERVICE_AREA_LOADED, onChange);
}

/**
 * Delivery-area state for the current storefront.
 * Pass an explicit outletId on pages that receive one as a prop — the tenant
 * context is only populated after the landing page has rendered.
 */
export function useServiceArea(outletIdOverride?: string): UseServiceArea {
  const { outletId } = useTenant();
  const { userId, token } = useAuth();
  const id = outletIdOverride || outletId;
  // The version makes this re-read once the store's own setting has arrived.
  const version = useSyncExternalStore(subscribeAreas, getServiceAreaVersion, () => 0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const area = useMemo(() => getServiceArea(id), [id, version]);
  // Server render has no localStorage — treat it as "no address picked yet".
  const city = useSyncExternalStore(subscribe, getSelectedCity, () => null);
  const coordsRaw = useSyncExternalStore(subscribe, getSelectedCoordsRaw, () => null);
  const coords = useMemo(() => parseCoords(coordsRaw), [coordsRaw]);

  useEffect(() => {
    void loadServiceArea(id);
  }, [id]);

  /* A guest who logs in mid-checkout has a saved address but nothing mirrored
   * yet, so resolve it here rather than waiting on the Header. */
  useEffect(() => {
    if (!area || city || !userId || !token) return;
    let cancelled = false;
    getAddresses(token, userId).then((list) => {
      if (cancelled || list.length === 0) return;
      const storedId = Number(localStorage.getItem("selected_address_id"));
      const selected = (storedId ? list.find((a) => a.id === storedId) : null) ?? list[0];
      setSelectedCity(addressLocationText(selected));
      window.dispatchEvent(new Event("selected_city_changed"));
    }).catch(() => { /* non-fatal — gate stays open until an address is picked */ });
    return () => { cancelled = true; };
  }, [area, city, userId, token]);

  return { area, city, blocked: !!area && !isCityServiceable(area, city, coords) };
}
